// Temporal-voting lock (spec §6 — implemented exactly; constants live in TUNING).
// Never trust a single frame: a plate locks only when the ring buffer agrees.

import type { OcrRead } from '../types';
import { TUNING } from '../types';
import { matchPattern, normalize } from './normalize';

interface BufferedRead {
  text: string;
  patternId: string;
  fallback: boolean;
  confs: number[];
  region?: { name: string; conf: number } | undefined;
  at: number;
}

export interface VoteState {
  status: 'idle' | 'candidate' | 'locked';
  /** Current best guess (per-position winners of the dominant group). */
  text: string;
  /** Per-position winner vote share (0..1) — drives the ghosting UI. */
  charShares: number[];
  /** Mean winner confidence at lock time. */
  confidence: number;
  patternId: string | null;
  region?: { name: string; conf: number } | undefined;
  bufferLen: number;
  /** Reads in the dominant (length, pattern) group. */
  groupLen: number;
}

export interface VoteTableRow {
  pos: number;
  votes: { ch: string; share: number }[];
}

const IDLE: VoteState = {
  status: 'idle',
  text: '',
  charShares: [],
  confidence: 0,
  patternId: null,
  bufferLen: 0,
  groupLen: 0,
};

export class VotingEngine {
  private buffer: BufferedRead[] = [];
  private lastAcceptAt = 0;

  /** Raw reads seen since last reset (debug overlay). */
  readonly recentRaw: { raw: string; conf: number; accepted: boolean }[] = [];

  reset(): void {
    this.buffer.length = 0;
    this.recentRaw.length = 0;
    this.lastAcceptAt = 0;
  }

  /**
   * Feed one OCR read. Returns the new vote state.
   * `now` is injectable for tests.
   */
  addRead(read: OcrRead, now: number = performance.now()): VoteState {
    // §6.6: stale buffer — a gap longer than resetAfterMs starts a fresh vote.
    if (this.lastAcceptAt > 0 && now - this.lastAcceptAt > TUNING.resetAfterMs) {
      this.buffer.length = 0;
    }

    const meanConf =
      read.charConfs.length > 0
        ? read.charConfs.reduce((a, b) => a + b, 0) / read.charConfs.length
        : 0;
    const accepted = meanConf >= TUNING.cMin;
    this.pushRaw(read.raw, meanConf, accepted);
    if (!accepted) return this.state();

    const match = matchPattern(normalize(read.raw));
    if (!match) return this.state();

    this.lastAcceptAt = now;
    this.buffer.push({
      text: match.text,
      patternId: match.patternId,
      fallback: match.fallback,
      confs: read.charConfs,
      region: read.region,
      at: now,
    });
    if (this.buffer.length > TUNING.bufferSize) this.buffer.shift();
    return this.state();
  }

  /** Called when the tracked box is lost or the candidate switches (§6.6). */
  notifyTrackLost(): void {
    this.reset();
  }

  private pushRaw(raw: string, conf: number, accepted: boolean): void {
    this.recentRaw.push({ raw, conf, accepted });
    if (this.recentRaw.length > 8) this.recentRaw.shift();
  }

  private dominantGroup(): BufferedRead[] {
    const groups = new Map<string, BufferedRead[]>();
    for (const r of this.buffer) {
      const key = `${r.patternId}:${r.text.length}`;
      const g = groups.get(key);
      if (g) g.push(r);
      else groups.set(key, [r]);
    }
    let best: BufferedRead[] = [];
    for (const g of groups.values()) if (g.length > best.length) best = g;
    return best;
  }

  /** Per-position confidence-weighted tallies of the dominant group. */
  voteTable(): VoteTableRow[] {
    const group = this.dominantGroup();
    if (group.length === 0) return [];
    const len = group[0]?.text.length ?? 0;
    const rows: VoteTableRow[] = [];
    for (let i = 0; i < len; i++) {
      const tally = new Map<string, number>();
      let total = 0;
      for (const r of group) {
        const ch = r.text[i] as string;
        const wt = r.confs[i] ?? 0.5;
        tally.set(ch, (tally.get(ch) ?? 0) + wt);
        total += wt;
      }
      rows.push({
        pos: i,
        votes: [...tally.entries()]
          .map(([ch, wtSum]) => ({ ch, share: total > 0 ? wtSum / total : 0 }))
          .sort((a, b) => b.share - a.share),
      });
    }
    return rows;
  }

  state(): VoteState {
    const group = this.dominantGroup();
    if (group.length === 0) return { ...IDLE, bufferLen: this.buffer.length };

    const rows = this.voteTable();
    const winners = rows.map((r) => r.votes[0] ?? { ch: '?', share: 0 });
    const text = winners.map((w) => w.ch).join('');
    const charShares = winners.map((w) => w.share);
    const fallback = group.some((r) => r.fallback);
    const needShare = fallback ? TUNING.lockShareFallback : TUNING.lockShare;

    // §6.5 lock condition: enough agreeing reads + every position's winner holds
    // the required weighted share + the string matches a pattern (by construction).
    const locked =
      group.length >= TUNING.lockMinReads && charShares.every((s) => s >= needShare);

    // Region: confidence-weighted majority across the group.
    const regionTally = new Map<string, number>();
    for (const r of group) {
      if (r.region) regionTally.set(r.region.name, (regionTally.get(r.region.name) ?? 0) + r.region.conf);
    }
    let region: VoteState['region'];
    if (regionTally.size > 0) {
      const [name, wt] = [...regionTally.entries()].sort((a, b) => b[1] - a[1])[0] as [
        string,
        number,
      ];
      region = { name, conf: wt / group.length };
    }

    const meanWinnerConf =
      group.reduce((acc, r) => acc + r.confs.reduce((a, b) => a + b, 0) / r.confs.length, 0) /
      group.length;

    return {
      status: locked ? 'locked' : 'candidate',
      text,
      charShares,
      confidence: Math.min(meanWinnerConf, charShares.reduce((a, b) => a + b, 0) / charShares.length),
      patternId: group[0]?.patternId ?? null,
      region,
      bufferLen: this.buffer.length,
      groupLen: group.length,
    };
  }
}
